"use client";

import { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/navigation";
import { CaretDown, CloudArrowUp } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
	DEFAULT_MANUFACTURING_FAQ_CONFIG,
	SERVICE_MANUFACTURING_FAQ_CONFIG,
} from "@/lib/serviceManufacturingFaqConfig";

// 生成 FAQ 数据的工厂函数
const generateFaqData = (count: number, prefix = "") =>
	Array.from({ length: count }, (_, i) => ({
		id: `q${i + 1}`,
		questionKey: prefix ? `${prefix}Q${i + 1}` : `q${i + 1}`,
		answerKey: prefix ? `${prefix}A${i + 1}` : `a${i + 1}`,
	}));

// FAQ 数据项类型
interface FAQDataItem {
	id: string;
	questionKey: string;
	answerKey: string;
}

// FAQ项组件
function FAQItem({
	faq,
	t,
	isOpen,
	onToggle,
}: {
	faq: FAQDataItem;
	t: (key: string) => string;
	isOpen: boolean;
	onToggle: () => void;
}) {
	return (
		<div className="border-b border-gray-200">
			<button
				onClick={onToggle}
				className="group flex w-full items-center justify-between py-5 text-left transition-colors hover:text-themePrimary400"
				aria-expanded={isOpen}
			>
				<span className="pr-4 text-base font-semibold text-black transition-colors group-hover:text-themePrimary400 sm:text-lg">
					{t(faq.questionKey)}
				</span>
				<CaretDown
					size={20}
					weight="bold"
					className={`flex-shrink-0 text-gray-500 transition-transform duration-300 ${
						isOpen ? "rotate-180" : ""
					}`}
				/>
			</button>
			<div
				className={`overflow-hidden transition-all duration-300 ease-in-out ${
					isOpen ? "max-h-[500px] pb-5" : "max-h-0"
				}`}
			>
				<p className="text-sm leading-relaxed text-black md:text-base">{t(faq.answerKey)}</p>
			</div>
		</div>
	);
}

interface ManufacturingFAQsProps {
	slug?: string;
	className?: string;
}

export default function ManufacturingFAQs({ slug, className }: ManufacturingFAQsProps) {
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	// 获取当前页面配置
	const customConfig = slug ? SERVICE_MANUFACTURING_FAQ_CONFIG[slug] : undefined;
	const config = customConfig || DEFAULT_MANUFACTURING_FAQ_CONFIG;
	const isCustomPage = Boolean(customConfig);

	// 生成 FAQ 数据（使用 useMemo 避免重复计算）
	const faqsData = useMemo(
		() => generateFaqData(config.faqCount, isCustomPage ? config.keyPrefix : ""),
		[config.faqCount, config.keyPrefix, isCustomPage],
	);

	// 翻译函数
	const t = useTranslations(config.namespace);
	const tBase = useTranslations("base");

	const handleToggle = (index: number) => {
		setOpenIndex(openIndex === index ? null : index);
	};

	return (
		<section className={cn("bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				<div className="flex flex-col gap-10 lg:flex-row lg:items-stretch lg:gap-16">
					{/* 左侧标题和按钮区域 */}
					<div className="flex w-full flex-col justify-between lg:w-[40%]">
						{/* 标题区域 */}
						<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
							<h2 className="relative mb-3 inline-block text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
								{t(config.titleKey)}
								{/* 蓝色下划线 */}
								<span className="absolute -bottom-4 left-0 h-[2px] w-full max-w-[300px] bg-themePrimary400 sm:max-w-[500px]"></span>
							</h2>
						</div>

						{/* 按钮区域 */}
						<div className="flex flex-wrap gap-4 pb-20">
							<Link
								href="/upload"
								target="_blank"
								className="inline-flex items-center justify-center gap-2 rounded-lg bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:bg-[#83b751] hover:text-[#f7ffee] hover:shadow-lg sm:px-8 sm:py-4 sm:text-base"
							>
								<CloudArrowUp size={20} weight="bold" />
								{tBase("getInstantQuote")}
							</Link>
							<Link
								href="/faqs"
								target="_blank"
								className="inline-flex items-center justify-center rounded-lg bg-[#107e03] px-6 py-3 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:bg-[#138f05] hover:text-[#f0fff0] hover:shadow-lg sm:px-8 sm:py-4 sm:text-base"
							>
								{t(config.moreKey)}
							</Link>
						</div>
					</div>

					{/* 右侧FAQ列表 */}
					<div className="w-full lg:w-[60%]">
						<div className="rounded-xl">
							{faqsData.map((faq, index) => (
								<FAQItem
									key={faq.id}
									faq={faq}
									t={t}
									isOpen={openIndex === index}
									onToggle={() => handleToggle(index)}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
